import React from 'react';
import { useTranslation } from 'react-i18next';
import { Image, Text, Box } from '@user-portal/wikr-components';

import OutlinedButton from 'components/OutlinedButton';
import Modal from 'components/Modal';
import { Cross, PlanIcon, MoveBack } from 'components/Icon';

import * as S from './styled';

import mail from './img/mail.webp';
import check from './img/check.webp';
import bankCard from './img/bankCard.webp';

import { ReminderOfferModalProps } from './types';

import { isTabletDevice } from 'theme/verifiers';

const ReminderOfferModal = ({
    onSubmit,
    onDiscard,
    onClose,
    onMoveBack,
    trackLoad,
    subscriptionPeriod,
}: ReminderOfferModalProps) => {
    const { t } = useTranslation();

    return (
        <Modal
            paddingX={0}
            paddingY={0}
            isOpen
            onClose={onClose}
            transparentOverlay={false}
            transparentBackground
            trackLoad={trackLoad}
        >
            <S.ReminderModalContent data-locator="reminderOfferContainer">
                <S.ModalHeaderTitle>
                    <S.IconWrapper onClick={onMoveBack} data-locator="cancelSubscriptionModalBackButton">
                        <MoveBack />
                    </S.IconWrapper>
                    <S.TitleText>
                        <Text type="large-text" medium>
                            {t('subscription.text.cancel')}
                        </Text>
                    </S.TitleText>
                    <S.IconWrapper onClick={onClose} data-locator="cancelSubscriptionModalCloseButton">
                        <Cross />
                    </S.IconWrapper>
                </S.ModalHeaderTitle>

                <Text type="h4" medium center mb={14}>
                    {t('subscription.reminder.titleComputed', { subscription: subscriptionPeriod })}
                </Text>
                <Text type="large-text" center mb={34}>
                    {t('subscription.reminder.subtitle')}
                </Text>

                <S.StepsWrapper>
                    <S.Line />
                    <S.Step data-locator="reminderOfferFullAccessStep">
                        <S.StepIconWrapper>
                            <Image src={check} alt="check" maxWidth={26} />
                        </S.StepIconWrapper>
                        <Box $paddingTop={12}>
                            <Text mb={20} type="h6" medium>
                                {t('subscription.reminder.steps.firstTitle')}
                            </Text>
                            <S.FirstStepBlock>
                                <S.FirstStepBlockTitle>
                                    <PlanIcon />
                                    <S.FirstStepBlockText type="large-text" medium>
                                        {t('basics.plan')}
                                    </S.FirstStepBlockText>
                                </S.FirstStepBlockTitle>
                                <Text type="small-text">{t('subscription.reminder.steps.firstSubtitle')}</Text>
                            </S.FirstStepBlock>
                        </Box>
                    </S.Step>
                    <S.Step data-locator="reminderOfferReminderStep">
                        <S.StepIconWrapper>
                            <Image src={mail} alt="mail" maxWidth={30} />
                        </S.StepIconWrapper>
                        <div>
                            <Text mb={isTabletDevice ? 32 : 8} type="h6" medium>
                                {t('subscription.reminder.steps.secondTitleComputed', {
                                    subscription: subscriptionPeriod,
                                })}
                            </Text>
                            <Text type="small-text">{t('subscription.reminder.steps.secondSubtitle')}</Text>
                        </div>
                    </S.Step>
                    <S.Step data-locator="reminderOfferChargeDateStep">
                        <S.StepIconWrapper>
                            <Image src={bankCard} alt="card" maxWidth={30} />
                        </S.StepIconWrapper>
                        <Box $paddingTop={12}>
                            <Text mb={20} type="h6" medium>
                                {t('subscription.reminder.steps.thirdTitle')}
                            </Text>
                            <Text type="small-text">{t('subscription.reminder.steps.thirdSubtitle')}</Text>
                        </Box>
                    </S.Step>
                </S.StepsWrapper>

                <S.SetReminderButton mb={16} onClick={onSubmit} dataLocator="reminderOfferSetReminderButton">
                    {t('subscription.reminder.setReminder')}
                </S.SetReminderButton>
                <OutlinedButton
                    dataLocator="reminderOfferCancelSubscriptionButton"
                    onClick={onDiscard}
                    textColor="action-main"
                >
                    {t('subscription.text.cancel')}
                </OutlinedButton>
            </S.ReminderModalContent>
        </Modal>
    );
};

export default ReminderOfferModal;
